import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from './ThemeContext';
import type { ThemeId } from './themes';
import { CustomThemeModal } from './CustomThemeModal';

/**
 * Pop-over theme picker for the nav bar.
 *
 * A small button shows the current theme; clicking opens a menu of all
 * available themes. Keyboard support: Esc closes, Enter/Space activates,
 * Tab cycles through the list normally.
 *
 * "Custom theme..." opens a modal where the user picks four base colors;
 * saving switches the active theme to `'custom'` and persists the palette.
 * The picker closes when the modal opens and re-opens when the modal closes,
 * so the user always lands back in the picker (which now lists "Custom").
 */
export function ThemeSelector(): JSX.Element {
  const { t } = useTranslation();
  const { themeId, themes, customPalette, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // When the modal is open, suppress the outside-click handler from inadvertently
  // closing the (already closed) picker, and remember to reopen the picker on close.
  const reopenAfterModalRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const current = themes.find((t) => t.id === themeId) ?? themes[0];

  // Close on outside click + Escape — but only when the modal isn't on top.
  useEffect(() => {
    if (!open || modalOpen) return;
    const onPointer = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, modalOpen]);

  const choose = (id: ThemeId): void => {
    setTheme(id);
    setOpen(false);
  };

  const openCustomModal = (): void => {
    reopenAfterModalRef.current = true;
    setOpen(false);
    setModalOpen(true);
  };

  const onModalClose = (): void => {
    setModalOpen(false);
    if (reopenAfterModalRef.current) {
      reopenAfterModalRef.current = false;
      setOpen(true);
    }
  };

  // Render the saved custom palette's swatch using CSS variables so the
  // hardcoded `.theme-swatch.theme-custom` rules can pick them up.
  const customSwatchStyle = customPalette
    ? ({
        '--swatch-custom-bg': customPalette.bg,
        '--swatch-custom-surface': customPalette.surface,
        '--swatch-custom-accent': customPalette.accent,
      } as React.CSSProperties)
    : undefined;

  return (
    <div className="theme-selector" ref={rootRef}>
      <button
        type="button"
        className="icon-btn theme-selector__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('theme.triggerLabel', { label: t(`theme.names.${current.id}`, current.label) })}
        onClick={() => setOpen((o) => !o)}
      >
        <PaletteIcon />
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t('theme.menuLabel')}
          className="theme-selector__menu"
          style={customSwatchStyle}
        >
          {themes.map((th) => {
            const selected = th.id === themeId;
            return (
              <button
                key={th.id}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                className={`theme-selector__item ${selected ? 'is-selected' : ''}`}
                onClick={() => choose(th.id)}
              >
                <span className="theme-selector__swatches" aria-hidden>
                  <span className={`theme-swatch theme-swatch--bg theme-${th.id}`} />
                  <span className={`theme-swatch theme-swatch--surface theme-${th.id}`} />
                  <span className={`theme-swatch theme-swatch--accent theme-${th.id}`} />
                </span>
                <span className="theme-selector__labels">
                  <span className="theme-selector__name">{t(`theme.names.${th.id}`, th.label)}</span>
                  <span className="theme-selector__desc">{t(`theme.descriptions.${th.id}`, th.description)}</span>
                </span>
                {selected && <span className="theme-selector__check" aria-hidden>✓</span>}
              </button>
            );
          })}

          <button
            type="button"
            role="menuitem"
            className="theme-selector__custom-trigger"
            onClick={openCustomModal}
          >
            <span>{customPalette ? t('theme.editCustom') : t('theme.createCustom')}</span>
            <span aria-hidden>›</span>
          </button>
        </div>
      )}

      <CustomThemeModal open={modalOpen} onClose={onModalClose} />
    </div>
  );
}

function PaletteIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
      <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
      <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
      <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </svg>
  );
}
